import React from "react";
import { Star, X } from "lucide-react";

import { cn } from "@/lib/utils";
import type { Localizer } from "@/main/react/Types";
import { useMediaQueryMatches } from "@/main/react/hooks/useMediaQueryMatches";
import {
    REVIEW_RATING_HALF_STARS_MAX,
    REVIEW_RATING_HALF_STARS_MIN,
} from "@/main/react/discussion/facet/ReviewFacetTypes";

const STAR_COUNT = 5;

/** A pointer that can hover, i.e. one where a preview and a commit are different gestures. */
const FINE_POINTER_MEDIA_QUERY = "(pointer: fine)";

/**
 * Half-stars -> the star number a human reads. Exact by construction: half-stars are integers, so
 * the single division happens here and nowhere else, and the result is always one of 0.5 .. 5.0.
 */
export function halfStarsToDisplayStars(halfStars: number): number {
    return halfStars / 2;
}

/**
 * Is the pointer capable of hovering? Gates the hover preview so a touch user does not get a
 * rating "previewed" by the tap that is about to set it — on a coarse pointer the preview and the
 * commit are the same gesture, so the preview is noise at best and a misread value at worst.
 *
 * <p>Defaults to `false` when `matchMedia` is unavailable (jsdom, SSR): a missing preview is
 * invisible, a spurious one is a wrong number on screen. That is also `useMediaQueryMatches`'s
 * `NO_MATCH` snapshot, so delegating to it (2026-08-15) preserved this default exactly while removing
 * the `useState` mirror `AGENTS.md` rule 31 forbids.
 */
function useHasFinePointer(): boolean {
    return useMediaQueryMatches(FINE_POINTER_MEDIA_QUERY);
}

export type HalfStarRatingInputProps = {
    localizer: Localizer;
    /** Integer half-stars, 1..10. `null` is "not rated yet" — never 0, which the DB CHECK forbids. */
    value: number | null;
    onChange: (halfStars: number | null) => void;
    disabled?: boolean;
    /** Renders the label + help lines. Off inside a dialog that already carries them. */
    showLabel?: boolean;
    dataPlaywrightId?: string;
    className?: string;
};

/**
 * The REVIEW facet's 1..10 half-star rating control.
 *
 * <p>There is no star or rating component anywhere else in the tree, so this is built from
 * `lucide-react`'s `Star` plus a width-clipped overlay rather than from a `StarHalf` glyph. The
 * clip is what makes a half and a full star the *same* shape at two fill widths — a `StarHalf`
 * glyph is a differently-drawn icon, so the two would not line up at the same optical weight.
 *
 * <p><b>Accessibility.</b> One `role="slider"` for the whole control, not ten buttons: the value is
 * a single number on a continuous scale, and ten separate controls would make a screen reader
 * announce ten things where the user is choosing one. Arrow keys move by one half-star and clamp;
 * `aria-valuetext` names the value in words ("4.5 out of 5 stars") because `aria-valuenow` is in
 * half-stars, which is an implementation unit no user thinks in.
 *
 * <p>Design language (AGENTS.md rule 23): the shared focus ring, no bespoke highlight, borders
 * rather than shadows.
 */
export default function HalfStarRatingInput({
    localizer,
    value,
    onChange,
    disabled = false,
    showLabel = true,
    dataPlaywrightId = "review-rating-input",
    className,
}: HalfStarRatingInputProps) {
    const hasFinePointer = useHasFinePointer();
    const [hoverHalfStars, setHoverHalfStars] = React.useState<number | null>(null);

    // The hover preview must never survive the pointer leaving, and must never apply on a coarse
    // pointer even if a stray mouse event arrives (some hybrid devices emit both).
    const previewHalfStars = hasFinePointer && !disabled && hoverHalfStars != null ? hoverHalfStars : null;
    const renderedHalfStars = previewHalfStars ?? value ?? 0;

    const clamp = React.useCallback(
        (next: number) => Math.min(REVIEW_RATING_HALF_STARS_MAX, Math.max(REVIEW_RATING_HALF_STARS_MIN, next)),
        [],
    );

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (disabled) {
                return;
            }
            // An unrated control starts one step below the minimum so the first ArrowRight lands on
            // 1 (half a star) rather than on 2 — the first press should reach the smallest value.
            const current = value ?? REVIEW_RATING_HALF_STARS_MIN - 1;
            switch (event.key) {
                case "ArrowRight":
                case "ArrowUp":
                    event.preventDefault();
                    onChange(clamp(current + 1));
                    return;
                case "ArrowLeft":
                case "ArrowDown":
                    event.preventDefault();
                    onChange(clamp(current - 1));
                    return;
                case "Home":
                    event.preventDefault();
                    onChange(REVIEW_RATING_HALF_STARS_MIN);
                    return;
                case "End":
                    event.preventDefault();
                    onChange(REVIEW_RATING_HALF_STARS_MAX);
                    return;
                case "Delete":
                case "Backspace":
                    event.preventDefault();
                    onChange(null);
                    return;
                default:
            }
        },
        [clamp, disabled, onChange, value],
    );

    const valueText = value == null
        ? localizer("discussion.facet.review.rating.required")
        : localizer("discussion.facet.review.rating.value", halfStarsToDisplayStars(value));

    return (
        <div className={cn("flex flex-col gap-1.5", className)} data-playwright-id={dataPlaywrightId}>
            {showLabel ? (
                <span className="text-sm font-medium text-foreground">
                    {localizer("discussion.facet.review.rating.label")}
                </span>
            ) : null}

            {/* `DESIGN-SYSTEM-4`. `flex-wrap` + `shrink-0` on the two rigid children is the whole
                fix, and the roles are not interchangeable: the slider's min-content is ~168px (five
                `size-8` spans) so it can never give way, and the clear pill must not either — without
                `shrink-0` the flex algorithm took the entire deficit out of it and wrapped "Clear
                rating" onto four lines inside a rounded pill. The value label is the one child that
                may shrink, so it is the only one carrying `min-w-0`. Pinned by the
                `narrow-container layout` block in this component's spec. */}
            <div className="flex flex-wrap items-center gap-2">
                <div
                    role="slider"
                    tabIndex={disabled ? -1 : 0}
                    aria-label={localizer("discussion.facet.review.rating.label")}
                    aria-valuemin={REVIEW_RATING_HALF_STARS_MIN}
                    aria-valuemax={REVIEW_RATING_HALF_STARS_MAX}
                    aria-valuenow={value ?? undefined}
                    aria-valuetext={valueText}
                    aria-disabled={disabled || undefined}
                    onKeyDown={handleKeyDown}
                    onMouseLeave={() => setHoverHalfStars(null)}
                    data-playwright-id={`${dataPlaywrightId}-slider`}
                    className={cn(
                        "inline-flex shrink-0 items-center rounded-full px-1 py-0.5",
                        "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring",
                        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
                    )}
                >
                    {Array.from({ length: STAR_COUNT }, (_unused, starIndex) => {
                        const leftHalfStars = starIndex * 2 + 1;
                        const rightHalfStars = starIndex * 2 + 2;
                        // How much of THIS star is painted, 0 / 0.5 / 1 — derived from the rendered
                        // value so the hover preview and the committed value use one code path.
                        const filledFraction = Math.min(1, Math.max(0, (renderedHalfStars - starIndex * 2) / 2));

                        return (
                            <span key={starIndex} className="relative inline-flex size-8 items-center justify-center">
                                <Star className="size-6 text-muted-foreground/50" aria-hidden="true" />
                                {filledFraction > 0 ? (
                                    <span
                                        className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden"
                                        // Clipping the SAME glyph is what keeps a half and a full star
                                        // optically identical; a StarHalf icon is a different drawing.
                                        style={{ clipPath: `inset(0 ${(1 - filledFraction) * 100}% 0 0)` }}
                                        aria-hidden="true"
                                    >
                                        <Star className="size-6 fill-primary text-primary" />
                                    </span>
                                ) : null}

                                {/* Two transparent hit targets per star. They are the only clickable
                                    surface; the slider above owns the keyboard. */}
                                <button
                                    type="button"
                                    tabIndex={-1}
                                    disabled={disabled}
                                    aria-hidden="true"
                                    className="absolute inset-y-0 left-0 w-1/2 disabled:cursor-not-allowed"
                                    data-playwright-id={`${dataPlaywrightId}-half-${leftHalfStars}`}
                                    onMouseEnter={() => setHoverHalfStars(leftHalfStars)}
                                    onClick={() => onChange(leftHalfStars)}
                                />
                                <button
                                    type="button"
                                    tabIndex={-1}
                                    disabled={disabled}
                                    aria-hidden="true"
                                    className="absolute inset-y-0 right-0 w-1/2 disabled:cursor-not-allowed"
                                    data-playwright-id={`${dataPlaywrightId}-half-${rightHalfStars}`}
                                    onMouseEnter={() => setHoverHalfStars(rightHalfStars)}
                                    onClick={() => onChange(rightHalfStars)}
                                />
                            </span>
                        );
                    })}
                </div>

                <span className="min-w-0 text-sm text-muted-foreground" data-playwright-id={`${dataPlaywrightId}-value`}>
                    {value == null ? null : localizer("discussion.facet.review.rating.value", halfStarsToDisplayStars(value))}
                </span>

                {value != null && !disabled ? (
                    <button
                        type="button"
                        onClick={() => onChange(null)}
                        data-playwright-id={`${dataPlaywrightId}-clear`}
                        className={cn(
                            "relative touch-hit-44 inline-flex shrink-0 items-center gap-1 rounded-full border border-border px-2 py-1 text-xs text-muted-foreground transition-colors",
                            "hover:bg-accent hover:text-foreground",
                            "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring",
                        )}
                    >
                        <X className="size-3.5" aria-hidden="true" />
                        {localizer("discussion.facet.review.rating.clear")}
                    </button>
                ) : null}
            </div>

            {showLabel ? (
                <span className="text-xs text-muted-foreground">
                    {localizer("discussion.facet.review.rating.help")}
                </span>
            ) : null}
        </div>
    );
}
